/**
 * 사진 파일에서 촬영일을 읽는다.
 *
 * 왜 여기서 읽나
 * -------------
 * 방문일자를 사람이 매번 고르는 게 번거롭다. 사진에는 찍힌 시각이 들어 있으니
 * 그걸 기본값으로 쓰면 대개 맞다.
 *
 * 문제는 그 값이 금방 사라진다는 것이다. 업로드 전에 사진을 두 번 다시 그리는데
 * (autoCropPhoto 로 비율 맞추기, downscalePhoto 로 크기 줄이기), 캔버스는
 * 픽셀만 옮기고 EXIF는 버린다. 서버의 ImageOptimizer 도 GD로 재인코딩하며 지운다.
 * 실제로 저장된 리뷰 사진에는 EXIF 세그먼트가 아예 없다.
 *
 * 그래서 **캔버스를 거치기 전에**, 갤러리에서 막 받아온 원본에서 읽어 둔다.
 *
 * 앱이 주는 사진에는 EXIF가 남아 있다 — image_picker 는 크기를 줄이면서도
 * 원본 메타데이터를 결과 파일에 다시 써 넣는다
 * (FLTImagePickerMetaDataUtil: CGImageDestinationAddImageFromSource(.., metadata)).
 *
 * 외부 라이브러리를 쓰지 않는다. 필요한 건 태그 하나뿐이고, 그걸 위해
 * 수백 KB짜리 EXIF 파서를 번들에 넣을 이유가 없다.
 */

/** JPEG 앞부분만 본다. EXIF는 파일 맨 앞 APP1 세그먼트에 있다. */
const MAX_HEADER_BYTES = 256 * 1024

/** EXIF 태그 번호 */
const TAG_DATETIME = 0x0132 // IFD0 DateTime (파일 수정 시각에 가까움)
const TAG_EXIF_IFD_POINTER = 0x8769
const TAG_DATETIME_ORIGINAL = 0x9003 // 셔터를 누른 시각 — 이게 제일 정확하다
const TAG_DATETIME_DIGITIZED = 0x9004

/**
 * 촬영일을 읽는다. 못 읽으면 null.
 *
 * @returns 'YYYY-MM-DD HH:mm' (기기 현지 시각으로 해석). EXIF에는 시간대가
 *   없어서 찍은 곳의 벽시계 시각으로 본다 — 방문일자로는 그게 맞다.
 */
export async function readPhotoTakenAt(file: File): Promise<string | null> {
  try {
    const head = file.slice(0, MAX_HEADER_BYTES)
    const buf = await head.arrayBuffer()
    const raw = parseExifDateTime(new DataView(buf))
    return raw ? toVisitDateFormat(raw) : null
  } catch {
    return null
  }
}

/** 'YYYY:MM:DD HH:MM:SS' → 'YYYY-MM-DD HH:mm' */
function toVisitDateFormat(exif: string): string | null {
  const m = exif.match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2})/)
  if (!m) return null
  const [, y, mo, d, h, mi] = m
  // 값이 비어 있는 사진도 있다 ('0000:00:00 00:00:00')
  if (y === '0000' || mo === '00' || d === '00') return null
  return `${y}-${mo}-${d} ${h}:${mi}`
}

/** JPEG 바이트에서 촬영일 문자열을 찾는다 */
function parseExifDateTime(view: DataView): string | null {
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) {
    return null // JPEG가 아니다
  }

  // 세그먼트를 훑어 APP1(0xFFE1) 중 'Exif\0\0' 로 시작하는 것을 찾는다
  let offset = 2
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) {
      return null // 세그먼트 경계가 깨졌다
    }
    const marker = view.getUint8(offset + 1)
    if (marker === 0xda) {
      return null // 이미지 데이터 시작 — 여기까지 없으면 없는 것
    }
    const size = view.getUint16(offset + 2)
    if (size < 2) return null

    if (marker === 0xe1 && offset + 10 <= view.byteLength) {
      const isExif =
        view.getUint32(offset + 4) === 0x45786966 && // 'Exif'
        view.getUint16(offset + 8) === 0x0000
      if (isExif) {
        return readTiff(view, offset + 10, offset + 2 + size)
      }
    }
    offset += 2 + size
  }
  return null
}

/** TIFF 헤더부터 읽어 원하는 태그를 찾는다 */
function readTiff(view: DataView, tiffStart: number, end: number): string | null {
  if (tiffStart + 8 > end || tiffStart + 8 > view.byteLength) return null

  const byteOrder = view.getUint16(tiffStart)
  const little = byteOrder === 0x4949 // 'II'
  if (!little && byteOrder !== 0x4d4d) return null // 'MM'
  if (view.getUint16(tiffStart + 2, little) !== 42) return null

  const ifd0 = tiffStart + view.getUint32(tiffStart + 4, little)

  // 셔터 시각이 제일 정확하다. Exif IFD 안에 있다.
  const exifIfdOffset = findTagValue(view, ifd0, tiffStart, little, end, TAG_EXIF_IFD_POINTER)
  if (typeof exifIfdOffset === 'number') {
    const original =
      readAscii(view, tiffStart + exifIfdOffset, tiffStart, little, end, TAG_DATETIME_ORIGINAL) ??
      readAscii(view, tiffStart + exifIfdOffset, tiffStart, little, end, TAG_DATETIME_DIGITIZED)
    if (original) return original
  }

  // 없으면 IFD0의 DateTime 으로 물러선다
  return readAscii(view, ifd0, tiffStart, little, end, TAG_DATETIME)
}

/** IFD를 훑어 태그의 숫자 값(오프셋 등)을 돌려준다 */
function findTagValue(
  view: DataView,
  ifd: number,
  tiffStart: number,
  little: boolean,
  end: number,
  wanted: number
): number | null {
  const entries = readEntryCount(view, ifd, little, end)
  if (entries === null) return null
  for (let i = 0; i < entries; i++) {
    const entry = ifd + 2 + i * 12
    if (entry + 12 > end) return null
    if (view.getUint16(entry, little) === wanted) {
      return view.getUint32(entry + 8, little)
    }
  }
  return null
}

/** IFD를 훑어 태그의 ASCII 값을 돌려준다 */
function readAscii(
  view: DataView,
  ifd: number,
  tiffStart: number,
  little: boolean,
  end: number,
  wanted: number
): string | null {
  const entries = readEntryCount(view, ifd, little, end)
  if (entries === null) return null

  for (let i = 0; i < entries; i++) {
    const entry = ifd + 2 + i * 12
    if (entry + 12 > end) return null
    if (view.getUint16(entry, little) !== wanted) continue

    const count = view.getUint32(entry + 4, little)
    if (count === 0 || count > 64) return null
    // 4바이트를 넘으면 값이 다른 곳에 있고, 그 자리엔 오프셋이 들어 있다
    const valueAt =
      count <= 4 ? entry + 8 : tiffStart + view.getUint32(entry + 8, little)
    if (valueAt + count > end || valueAt + count > view.byteLength) return null

    let out = ''
    for (let k = 0; k < count; k++) {
      const c = view.getUint8(valueAt + k)
      if (c === 0) break
      out += String.fromCharCode(c)
    }
    return out.trim() || null
  }
  return null
}

/** IFD의 항목 수. 범위를 벗어나면 null. */
function readEntryCount(
  view: DataView,
  ifd: number,
  little: boolean,
  end: number
): number | null {
  if (ifd < 0 || ifd + 2 > end || ifd + 2 > view.byteLength) return null
  const n = view.getUint16(ifd, little)
  // 터무니없이 크면 깨진 데이터다
  return n > 0 && n < 512 ? n : null
}
